import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from './AuthContext';
import { getApiBaseUrl } from './utils/api';
import Icon from './Icon';
import './Tickets.css';
import './Orders.css';

const DEFAULT_QUOTE_PROFIT_MARGIN = '15';
const DEFAULT_QUOTE_CATEGORY = 'Hardware med omvendt momspligt';
const CBC_ORDER_URL = 'https://cbcnet.dk/';

function getOrderNumber(order) {
  const value =
    order?.['zendesk-id'] ??
    order?.zendesk_id ??
    order?.zendeskId ??
    order?.id;
  if (value == null || value === '') return '—';
  return String(value);
}
const ORDER_WORKFLOW_STEPS = [
  { id: 'ticket', label: 'Create ticket', action: 'tickets' },
  { id: 'cbc', label: 'Create CBC order', action: 'cbc' },
  { id: 'quote', label: 'Create quote' },
  { id: 'accept', label: 'Customer accept' }
];

function OrderWorkflowProgress({ isAdmin }) {
  if (!isAdmin) return null;

  const handleStepClick = (step) => {
    if (step.action === 'tickets') {
      window.dispatchEvent(new CustomEvent('navigateToTickets'));
      return;
    }
    if (step.action === 'cbc') {
      window.open(CBC_ORDER_URL, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <section className="orders-card orders-workflow-card">
      <header className="orders-card-header">
        <h3>Order process</h3>
        <p>From ticket to customer acceptance</p>
      </header>
      <nav className="orders-workflow" aria-label="Order process">
      <ol className="orders-workflow-steps">
        {ORDER_WORKFLOW_STEPS.map((step, index) => {
          const isClickable = step.action === 'tickets' || step.action === 'cbc';
          const StepDot = isClickable ? 'button' : 'span';

          return (
            <li key={step.id} className="orders-workflow-step">
              {index > 0 && <span className="orders-workflow-connector" aria-hidden="true" />}
              <StepDot
                type={isClickable ? 'button' : undefined}
                className={`orders-workflow-dot${isClickable ? ' clickable' : ''}`}
                onClick={isClickable ? () => handleStepClick(step) : undefined}
                title={
                  step.action === 'tickets'
                    ? 'Go to Tickets'
                    : step.action === 'cbc'
                      ? 'Open CBCnet in a new tab'
                      : undefined
                }
                aria-label={step.label}
              />
              <span className="orders-workflow-label">{step.label}</span>
            </li>
          );
        })}
      </ol>
    </nav>
    </section>
  );
}

function formatQuoteAmount(value) {
  if (value == null || Number.isNaN(value)) return '—';
  return new Intl.NumberFormat('da-DK', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
}

/** Order JSON prices are ex-VAT (e-conomic uses ex-VAT amounts). */
function getLinePriceExVat(line) {
  if (!line) return null;
  if (line.priceExVat != null && line.priceExVat !== '') {
    const n = Number(line.priceExVat);
    return Number.isFinite(n) ? n : null;
  }
  if (line.priceIncVat != null && line.priceIncVat !== '') {
    const n = Number(line.priceIncVat);
    return Number.isFinite(n) ? n : null;
  }
  if (line.unitPrice !== '' && line.unitPrice != null && line.unitPrice !== undefined) {
    const n = Number(line.unitPrice);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/** Ex-VAT line amount: order-total price is not multiplied by qty. */
function lineExVatAmount(line) {
  const unitEx = getLinePriceExVat(line);
  if (unitEx == null || !Number.isFinite(unitEx)) return null;
  if (line.priceIsOrderTotal) {
    return unitEx;
  }
  const qty = Math.max(0, Number(line.quantity));
  const q = qty > 0 && Number.isFinite(qty) ? qty : 1;
  return unitEx * q;
}

/** Same rounding as server economicClient.roundMoney */
function roundQuoteMoney(n) {
  if (n == null || Number.isNaN(n)) return 0;
  return Math.round(Number(n) * 100) / 100;
}

/** Ex-VAT revenue when profit is margin % of that revenue: cost ÷ (1 − margin/100). */
function exVatSaleFromMarginOnSalePreview(costExVat, marginPct) {
  if (costExVat == null || !Number.isFinite(costExVat)) return null;
  const m = Number.isFinite(marginPct) ? Math.max(0, marginPct) : 0;
  if (m >= 100) return null;
  const d = 1 - m / 100;
  if (d <= 0) return null;
  return roundQuoteMoney(costExVat / d);
}

function getLineProfitMargin(line) {
  const raw = line?.profitMargin;
  if (raw === '' || raw == null || raw === undefined) return Number(DEFAULT_QUOTE_PROFIT_MARGIN);
  const n = Number(raw);
  return Number.isFinite(n) ? Math.max(0, n) : Number(DEFAULT_QUOTE_PROFIT_MARGIN);
}

function normalizeProductNumber(value) {
  if (value == null || value === '') return '';
  const s = String(value).trim().replace(',', '.');
  if (/^\d+(\.0+)?$/.test(s)) {
    return String(parseInt(s, 10));
  }
  const n = Number(s);
  if (Number.isFinite(n) && Number.isInteger(n)) {
    return String(n);
  }
  return String(value).trim();
}

function normalizeCategoryLabel(value) {
  return String(value ?? '')
    .replace(/\u00a0/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');
}

function categoriesMatch(a, b) {
  const na = normalizeCategoryLabel(a);
  const nb = normalizeCategoryLabel(b);
  if (!na || !nb) return false;
  if (na === nb) return true;
  if (na.toLowerCase() === nb.toLowerCase()) return true;
  return na.localeCompare(nb, 'da', { sensitivity: 'accent' }) === 0;
}

const Orders = () => {
  const { selectedTenant, authenticatedFetch, hasPrivilege, user } = useAuth();
  const isAdmin = (user?.role || '').toLowerCase() === 'admin';
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [quoteModalOpen, setQuoteModalOpen] = useState(false);
  const [quoteOrder, setQuoteOrder] = useState(null);
  const [quoteTitle, setQuoteTitle] = useState('');
  const [cbcLines, setCbcLines] = useState([]);
  const [productDescriptions, setProductDescriptions] = useState([]);
  const [economicCatalog, setEconomicCatalog] = useState([]);
  const [lineCategories, setLineCategories] = useState({});
  const [quoteModalLoading, setQuoteModalLoading] = useState(false);
  const [quoteModalError, setQuoteModalError] = useState(null);
  const [quoteSubmitting, setQuoteSubmitting] = useState(false);
  const [quoteSubmitError, setQuoteSubmitError] = useState(null);
  const [quoteSubmitSuccess, setQuoteSubmitSuccess] = useState(null);

  const acceptOfferBusyRef = useRef(false);
  const [acceptOfferBusyId, setAcceptOfferBusyId] = useState(null);

  const fetchOrders = useCallback(async () => {
    if (!selectedTenant) {
      setError('No tenant selected');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await authenticatedFetch(
        `${getApiBaseUrl()}/api/orders?tenant=${encodeURIComponent(selectedTenant.name)}`
      );

      const data = await response.json();

      if (!response.ok || !data.success) {
        setError(data.error || 'Failed to fetch orders');
        setOrders([]);
      } else {
        setOrders(Array.isArray(data.orders) ? data.orders : []);
      }
    } catch (e) {
      console.error('Error fetching orders:', e);
      setError('Network error. Please try again.');
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [selectedTenant, authenticatedFetch]);

  useEffect(() => {
    if (selectedTenant) {
      fetchOrders();
    } else {
      setOrders([]);
      setLoading(false);
    }
  }, [selectedTenant, fetchOrders]);

  const categorySelectOptions = useMemo(() => {
    const set = new Set(
      (productDescriptions || [])
        .map((d) => normalizeCategoryLabel(d))
        .filter(Boolean)
    );
    set.add(DEFAULT_QUOTE_CATEGORY);
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'da'));
  }, [productDescriptions]);

  const handleCloseQuoteModal = useCallback(() => {
    setQuoteModalOpen(false);
    setQuoteOrder(null);
    setQuoteTitle('');
    setCbcLines([]);
    setLineCategories({});
    setProductDescriptions([]);
    setEconomicCatalog([]);
    setQuoteModalLoading(false);
    setQuoteModalError(null);
    setQuoteSubmitError(null);
    setQuoteSubmitSuccess(null);
    setQuoteSubmitting(false);
  }, []);

  const handleCreateQuote = (order) => {
    const zendeskCol = Object.keys(order).find((k) => k.toLowerCase() === 'zendesk-id');
    const zendeskId = zendeskCol ? order[zendeskCol] : '';
    const defaultTitle = `Quote — ${order.subject || zendeskId || `#${order.id}`}`;
    setQuoteTitle(defaultTitle);
    setLineCategories({});
    setCbcLines([]);
    setProductDescriptions([]);
    setEconomicCatalog([]);
    setQuoteModalError(null);
    setQuoteSubmitError(null);
    setQuoteSubmitSuccess(null);
    setQuoteSubmitting(false);
    setQuoteOrder(order);
    setQuoteModalOpen(true);
  };

  useEffect(() => {
    if (!quoteModalOpen || !quoteOrder || !selectedTenant) return undefined;

    let cancelled = false;

    async function loadQuoteModalData() {
      setQuoteModalLoading(true);
      setQuoteModalError(null);

      try {
        const tenantQ = encodeURIComponent(selectedTenant.name);
        const orderIdEnc = encodeURIComponent(String(quoteOrder.id));
        const descUrl = `${getApiBaseUrl()}/api/economic-products/descriptions`;
        const catalogUrl = `${getApiBaseUrl()}/api/economic-products/catalog`;
        const cbcUrl = `${getApiBaseUrl()}/api/orders/${orderIdEnc}/cbc?tenant=${tenantQ}`;

        const [descRes, catalogRes, cbcRes] = await Promise.all([
          authenticatedFetch(descUrl),
          authenticatedFetch(catalogUrl),
          authenticatedFetch(cbcUrl),
        ]);

        const descJson = await descRes.json().catch(() => ({}));
        const catalogJson = await catalogRes.json().catch(() => ({}));
        const cbcJson = await cbcRes.json().catch(() => ({}));

        if (cancelled) return;

        if (descRes.ok && descJson.success) {
          setProductDescriptions(Array.isArray(descJson.descriptions) ? descJson.descriptions : []);
        } else {
          setProductDescriptions([]);
          console.warn('economic-products/descriptions:', descJson.error || descRes.statusText);
        }

        if (catalogRes.ok && catalogJson.success) {
          setEconomicCatalog(Array.isArray(catalogJson.products) ? catalogJson.products : []);
        } else {
          setEconomicCatalog([]);
          console.warn('economic-products/catalog:', catalogJson.error || catalogRes.statusText);
        }

        if (!cbcRes.ok || !cbcJson.success) {
          setCbcLines([]);
          const detail = cbcJson.detail;
          const detailStr =
            typeof detail === 'string' ? detail : detail != null ? JSON.stringify(detail) : '';
          setQuoteModalError(
            [cbcJson.error, detailStr].filter(Boolean).join(' — ') ||
              `Could not load order lines (${cbcRes.status}).`
          );
          return;
        }

        const rawLines = Array.isArray(cbcJson.lines) ? cbcJson.lines : [];
        const lines = rawLines.map((ln) => {
          const pFromApi =
            ln.priceExVat != null && ln.priceExVat !== '' && Number.isFinite(Number(ln.priceExVat))
              ? Number(ln.priceExVat)
              : ln.priceIncVat != null && ln.priceIncVat !== '' && Number.isFinite(Number(ln.priceIncVat))
                ? Number(ln.priceIncVat)
                : ln.unitPrice !== '' && ln.unitPrice != null && Number.isFinite(Number(ln.unitPrice))
                  ? Number(ln.unitPrice)
                  : null;
          return {
            ...ln,
            priceExVat: pFromApi,
            isManual: false,
            priceIsOrderTotal: !!ln.priceIsOrderTotal,
            profitMargin: DEFAULT_QUOTE_PROFIT_MARGIN
          };
        });
        setCbcLines(lines);

        const initialCats = {};
        lines.forEach((ln) => {
          if (ln != null && ln.key != null) initialCats[String(ln.key)] = DEFAULT_QUOTE_CATEGORY;
        });
        setLineCategories(initialCats);
      } catch (e) {
        if (!cancelled) {
          console.error('Quote modal load:', e);
          setQuoteModalError('Network error while loading quote data.');
          setCbcLines([]);
        }
      } finally {
        if (!cancelled) setQuoteModalLoading(false);
      }
    }

    loadQuoteModalData();
    return () => {
      cancelled = true;
    };
  }, [quoteModalOpen, quoteOrder, selectedTenant, authenticatedFetch]);

  const catalogByCategory = useMemo(() => {
    const map = new Map();
    for (const product of economicCatalog) {
      const cat = normalizeCategoryLabel(product.categoryDescription);
      if (!cat) continue;
      if (!map.has(cat)) map.set(cat, product);
      const catKey = cat.toLocaleLowerCase('da-DK');
      if (!map.has(catKey)) map.set(catKey, product);
    }
    return map;
  }, [economicCatalog]);

  const resolveCatalogProductForCategory = useCallback(
    (categoryValue) => {
      const cat = normalizeCategoryLabel(categoryValue);
      if (!cat) return null;
      const fromMap =
        catalogByCategory.get(cat) || catalogByCategory.get(cat.toLocaleLowerCase('da-DK'));
      if (fromMap) return fromMap;
      return (
        economicCatalog.find((p) => categoriesMatch(p.categoryDescription, cat)) || null
      );
    },
    [economicCatalog, catalogByCategory]
  );

  const quoteTotals = useMemo(() => {
    let exVatSum = 0;
    let withMarginSum = 0;
    let marginTooHigh = false;
    for (const line of cbcLines) {
      const lineEx = lineExVatAmount(line);
      if (lineEx == null || !Number.isFinite(lineEx)) continue;
      exVatSum += lineEx;
      const m = getLineProfitMargin(line);
      if (m >= 100) marginTooHigh = true;
      const net = exVatSaleFromMarginOnSalePreview(lineEx, m);
      if (net == null) marginTooHigh = true;
      else withMarginSum += net;
    }
    return { exVatSum, withMarginSum, marginTooHigh };
  }, [cbcLines]);

  const handleAddQuoteLine = useCallback(() => {
    const newKey = `manual-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    setCbcLines((prev) => [
      ...prev,
      {
        key: newKey,
        description: '',
        quantity: 1,
        unitPrice: '',
        currency: '',
        priceExVat: null,
        isManual: true,
        priceIsOrderTotal: false,
        profitMargin: DEFAULT_QUOTE_PROFIT_MARGIN,
        economicProductNumber: ''
      }
    ]);
    setLineCategories((prev) => ({ ...prev, [newKey]: DEFAULT_QUOTE_CATEGORY }));
  }, []);

  const handleRemoveQuoteLine = useCallback((key) => {
    setCbcLines((prev) => prev.filter((l) => String(l.key) !== String(key)));
    setLineCategories((prev) => {
      const next = { ...prev };
      delete next[String(key)];
      return next;
    });
  }, []);

  const handleQuoteLinePatch = useCallback((key, patch) => {
    setCbcLines((prev) =>
      prev.map((l) => (String(l.key) === String(key) ? { ...l, ...patch } : l))
    );
  }, []);

  const handlePriceExVatInput = useCallback((key, valueStr) => {
    if (valueStr === '' || valueStr == null) {
      handleQuoteLinePatch(key, { priceExVat: null, unitPrice: '' });
      return;
    }
    const n = Number(valueStr);
    if (Number.isFinite(n)) {
      handleQuoteLinePatch(key, { priceExVat: n, unitPrice: valueStr });
    }
  }, [handleQuoteLinePatch]);

  const applyCategoryCatalogAutofill = useCallback((lineKey, catalogProduct) => {
    if (!catalogProduct) return;
    setCbcLines((prev) =>
      prev.map((l) => {
        if (String(l.key) !== String(lineKey)) return l;
        const next = { ...l };
        const pn = normalizeProductNumber(catalogProduct.productNumber);
        if (pn) next.economicProductNumber = pn;

        if (!String(l.description || '').trim()) {
          next.description =
            catalogProduct.lineDescription || catalogProduct.categoryDescription || '';
        }

        const hasPrice = getLinePriceExVat(l) != null;
        if (!hasPrice && catalogProduct.priceExVat != null && Number.isFinite(catalogProduct.priceExVat)) {
          next.priceExVat = catalogProduct.priceExVat;
          next.unitPrice = String(catalogProduct.priceExVat);
        }

        return next;
      })
    );
  }, []);

  const handleLineCategoryChange = useCallback(
    (lineKey, value) => {
      const normalizedCategory = normalizeCategoryLabel(value);
      setLineCategories((prev) => ({ ...prev, [String(lineKey)]: normalizedCategory || value }));

      const catalogProduct = resolveCatalogProductForCategory(normalizedCategory || value);
      if (catalogProduct) {
        applyCategoryCatalogAutofill(lineKey, catalogProduct);
      }
    },
    [resolveCatalogProductForCategory, applyCategoryCatalogAutofill]
  );

  const handleLineMarginChange = useCallback(
    (lineKey, value) => {
      handleQuoteLinePatch(lineKey, { profitMargin: value });
    },
    [handleQuoteLinePatch]
  );

  const handleSubmitQuoteDraft = async (e) => {
    e.preventDefault();
    if (!selectedTenant || !quoteOrder || quoteSubmitting) return;

    setQuoteSubmitError(null);
    setQuoteSubmitSuccess(null);

    const titleTrimmed = quoteTitle.trim();
    if (!titleTrimmed) {
      setQuoteSubmitError('Quote title is required.');
      return;
    }

    if (!cbcLines.length) {
      setQuoteSubmitError('No order lines to submit.');
      return;
    }

    for (let i = 0; i < cbcLines.length; i += 1) {
      const m = getLineProfitMargin(cbcLines[i]);
      if (m >= 100) {
        setQuoteSubmitError(`Line ${i + 1}: profit margin must be less than 100% (share of selling price).`);
        return;
      }
    }

    setQuoteSubmitting(true);
    try {
      const body = {
        tenant: selectedTenant.name,
        ticketId: quoteOrder.id,
        quoteTitle: titleTrimmed,
        lines: cbcLines.map((line) => ({
          key: line.key,
          description: line.description,
          quantity: line.quantity,
          unitPrice: line.unitPrice,
          currency: line.currency,
          priceExVat: getLinePriceExVat(line),
          categoryDescription: lineCategories[String(line.key)] || DEFAULT_QUOTE_CATEGORY,
          isManual: !!line.isManual,
          priceIsOrderTotal: !!line.priceIsOrderTotal,
          profitMargin: getLineProfitMargin(line),
          economicProductNumber: line.economicProductNumber || ''
        })),
      };

      const response = await authenticatedFetch(`${getApiBaseUrl()}/api/orders/quote-draft`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok || !data.success) {
        setQuoteSubmitError(data.error || (typeof data.detail === 'string' ? data.detail : '') || 'Submit failed.');
        return;
      }

      setQuoteSubmitSuccess(
        data.quoteNumber != null && data.quoteNumber !== ''
          ? `${data.message || 'Quote draft created.'} (e-conomic #${data.quoteNumber})`
          : data.message || 'Submitted.'
      );
      fetchOrders();
    } catch (err) {
      console.error(err);
      setQuoteSubmitError('Network error.');
    } finally {
      setQuoteSubmitting(false);
    }
  };

  const handleAcceptOffer = useCallback(
    async (order) => {
      if (!selectedTenant?.name || !order?.id || acceptOfferBusyRef.current) return;

      acceptOfferBusyRef.current = true;
      setAcceptOfferBusyId(order.id);

      try {
        const response = await authenticatedFetch(`${getApiBaseUrl()}/api/orders/accept-offer`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            tenant: selectedTenant.name,
            cbcOrderId: String(order.id),
          }),
        });

        const data = await response.json().catch(() => ({}));

        if (!response.ok || !data.success) {
          const detail = data.detail;
          const nested =
            detail && typeof detail === 'object' && detail.message != null
              ? detail.message
              : detail;
          const msg =
            (typeof nested === 'string' ? nested : null) ||
            (typeof nested === 'object' ? JSON.stringify(nested) : null) ||
            (typeof data.error === 'string' ? data.error : null) ||
            'Could not update Zendesk.';
          window.alert(msg);
          return;
        }

        window.alert(
          'Thank you for your order!\n\nWe have received your acceptance and our team has been notified. We will proceed with ordering the equipment.'
        );
        fetchOrders();
      } catch (e) {
        console.error(e);
        window.alert('Network error.');
      } finally {
        acceptOfferBusyRef.current = false;
        setAcceptOfferBusyId(null);
      }
    },
    [selectedTenant, authenticatedFetch, fetchOrders]
  );

  const handleOpenQuotePdf = useCallback(
    async (e, order) => {
      e.preventDefault();
      if (!selectedTenant || !order) return;
      try {
        const params = new URLSearchParams({
          tenant: selectedTenant.name,
          cbcOrderId: String(order.id)
        });
        const response = await authenticatedFetch(
          `${getApiBaseUrl()}/api/orders/quote-pdf?${params.toString()}`
        );
        const ct = response.headers.get('content-type') || '';
        if (!response.ok) {
          let msg = 'Could not open quote PDF.';
          if (ct.includes('application/json')) {
            const data = await response.json().catch(() => ({}));
            msg =
              (typeof data.detail === 'string' ? data.detail : null) ||
              (typeof data.error === 'string' ? data.error : null) ||
              msg;
          }
          window.alert(msg);
          return;
        }
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank', 'noopener,noreferrer');
        setTimeout(() => URL.revokeObjectURL(url), 120000);
      } catch (err) {
        console.error(err);
        window.alert('Network error.');
      }
    },
    [selectedTenant, authenticatedFetch]
  );

  const sortedOrders = useMemo(() => {
    return [...orders].sort((a, b) => {
      const aValue = a.created_at ?? a.createdAt;
      const bValue = b.created_at ?? b.createdAt;
      if (!aValue && !bValue) return 0;
      if (!aValue) return 1;
      if (!bValue) return -1;
      return new Date(bValue).getTime() - new Date(aValue).getTime();
    });
  }, [orders]);

  if (loading) {
    return (
      <div className="orders-page">
        <OrderWorkflowProgress isAdmin={isAdmin} />
        <section className="orders-card orders-list-card">
          <div className="loading-container">
            <Icon name="tickets" size="large" className="loading-icon" />
            <p>Loading orders...</p>
          </div>
        </section>
      </div>
    );
  }

  if (error) {
    return (
      <div className="orders-page">
        <OrderWorkflowProgress isAdmin={isAdmin} />
        <section className="orders-card orders-list-card">
          <div className="error-container">
            <Icon name="tickets" size="large" className="error-icon" />
            <h3>Error Loading Orders</h3>
            <p>{error}</p>
            <button onClick={fetchOrders} className="retry-button">
              Retry
            </button>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="orders-page">
      <OrderWorkflowProgress isAdmin={isAdmin} />
      <section className="orders-card orders-list-card">
      <div className="tickets-header">
        <div className="tickets-title-section">
          <h2>Orders</h2>
        </div>
        <div className="tickets-actions">
          <span className="tickets-count">
            {sortedOrders.length} order{sortedOrders.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {orders.length === 0 ? (
        <div className="no-tickets-container">
          <Icon name="tickets" size="large" className="no-tickets-icon" />
          <h3>No Orders Found</h3>
          <p>No orders found.</p>
        </div>
      ) : (
        <div className="orders-table-wrap">
          <table className="orders-simple-table">
            <thead className="orders-table-sr-header">
              <tr>
                <th scope="col">Subject</th>
                <th scope="col">Order number</th>
                <th scope="col">Action</th>
              </tr>
            </thead>
            <tbody>
              {sortedOrders.map((order, index) => {
                const subjectValue = order.subject;
                const subjectText =
                  subjectValue != null && subjectValue !== ''
                    ? String(subjectValue)
                    : '—';

                return (
                  <tr key={order.id || index} className="orders-simple-row">
                    <td className="orders-simple-cell orders-simple-subject">
                      {order.downloadlink ? (
                        <button
                          type="button"
                          className="order-subject-download-link"
                          onClick={(e) => handleOpenQuotePdf(e, order)}
                        >
                          {subjectText}
                        </button>
                      ) : (
                        subjectText
                      )}
                    </td>
                    <td className="orders-simple-cell orders-simple-order-number">
                      {getOrderNumber(order)}
                    </td>
                    <td className="orders-simple-cell orders-simple-actions">
                      <div className="order-actions">
                        {hasPrivilege('orders', 'create') && (
                          <button
                            className="order-action-button create-quote-button"
                            onClick={() => handleCreateQuote(order)}
                            title="Create Quote"
                          >
                            Create Quote
                          </button>
                        )}
                        {hasPrivilege('orders', 'accept') &&
                          (String(order.quote || '').trim().toLowerCase() === 'accepted' ? (
                            <span
                              className="orders-offer-accepted-label"
                              title="This order was accepted in the portal"
                            >
                              Offer accepted
                            </span>
                          ) : (
                            <button
                              type="button"
                              className="order-action-button accept-offer-button"
                              onClick={() => handleAcceptOffer(order)}
                              disabled={
                                acceptOfferBusyId !== null && acceptOfferBusyId === order.id
                              }
                              title="Accept Offer"
                            >
                              {acceptOfferBusyId === order.id ? 'Submitting…' : 'Accept Offer'}
                            </button>
                          ))}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      </section>
      {quoteModalOpen && quoteOrder && createPortal(
        <div
          className="ticket-form-overlay quote-draft-overlay"
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) handleCloseQuoteModal();
          }}
        >
          <div
            className="ticket-form-modal quote-draft-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="quote-draft-title"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="ticket-form-header">
              <h3 id="quote-draft-title">Create quote</h3>
              <button
                type="button"
                className="ticket-form-close"
                onClick={handleCloseQuoteModal}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <form className="ticket-form" onSubmit={handleSubmitQuoteDraft}>
              <p style={{ margin: 0, fontSize: '0.9rem', opacity: 0.85 }}>
                {quoteOrder.subject || 'Order'}
                <span style={{ opacity: 0.6 }}>
                  {' '}
                  (order #{quoteOrder.id})
                </span>
              </p>

              <div className="ticket-form-field">
                <label htmlFor="quote-draft-title-field">Quote title</label>
                <input
                  id="quote-draft-title-field"
                  type="text"
                  value={quoteTitle}
                  onChange={(e) => setQuoteTitle(e.target.value)}
                  autoComplete="off"
                  disabled={quoteModalLoading}
                />
              </div>

              {quoteModalLoading && <p style={{ margin: 0 }}>Loading order lines and categories…</p>}
              {quoteModalError && <div className="ticket-form-error">{quoteModalError}</div>}
              {!quoteModalLoading && !quoteModalError && cbcLines.length === 0 && (
                <p style={{ margin: 0, fontSize: '0.88rem', color: 'rgba(255,255,255,0.55)' }}>
                  No lines were returned for this order. Use &quot;Add line&quot; to add rows manually.
                </p>
              )}
              {!quoteModalLoading && !productDescriptions.length && cbcLines.length > 0 && (
                <div className="ticket-form-error">
                  No categories found in economic-products (Description column empty or table missing).
                </div>
              )}

              {!quoteModalLoading && !quoteModalError && (
                <div className="ticket-form-field" style={{ gap: '0.75rem' }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      flexWrap: 'wrap',
                      gap: '0.5rem'
                    }}
                  >
                    <span style={{ fontSize: '0.9rem', fontWeight: 600, color: 'rgba(255, 255, 255, 0.85)' }}>
                      Order lines
                    </span>
                    <button
                      type="button"
                      className="quote-draft-add-line"
                      onClick={handleAddQuoteLine}
                      disabled={quoteModalLoading || quoteSubmitting}
                    >
                      + Add line
                    </button>
                  </div>

                  {cbcLines.length > 0 && (
                    <>
                      <div className="quote-draft-lines-scroll">
                        <div className="quote-draft-line-head">
                          <span>Line</span>
                          <span>Price (ex. VAT)</span>
                          <span>Margin %</span>
                          <span>Category</span>
                          <span aria-hidden="true" />
                        </div>
                        {cbcLines.map((line, lineIdx) => {
                          const priceEx = getLinePriceExVat(line);
                          const lineExVatLine = lineExVatAmount(line);
                          const lineMargin = line.profitMargin ?? DEFAULT_QUOTE_PROFIT_MARGIN;
                          return (
                            <div key={`quote-line-${String(line.key)}-${lineIdx}`} className="quote-draft-line-row">
                              <div>
                                <textarea
                                  className="quote-draft-desc-input quote-draft-desc-textarea"
                                  placeholder="Product description"
                                  rows={2}
                                  value={line.description || ''}
                                  onChange={(e) => handleQuoteLinePatch(line.key, { description: e.target.value })}
                                  disabled={quoteModalLoading || quoteSubmitting}
                                  aria-label={`Product description, line ${lineIdx + 1}`}
                                />
                                <div className="quote-draft-line-meta">
                                  {line.isManual ? (
                                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                                      Qty
                                      <input
                                        type="number"
                                        min="0.001"
                                        step="any"
                                        style={{
                                          width: '4.25rem',
                                          padding: '0.2rem 0.35rem',
                                          borderRadius: 6,
                                          border: '1px solid rgba(255,255,255,0.2)',
                                          background: 'rgba(13, 17, 27, 0.95)',
                                          color: '#f5f7fa',
                                          fontSize: '0.8rem'
                                        }}
                                        value={line.quantity ?? 1}
                                        onChange={(e) => handleQuoteLinePatch(line.key, { quantity: e.target.value })}
                                        disabled={quoteModalLoading || quoteSubmitting}
                                      />
                                    </label>
                                  ) : (
                                    <span>Qty: {line.quantity ?? 1}</span>
                                  )}
                                  {line.currency ? (
                                    <span style={{ marginLeft: '0.5rem' }}>{String(line.currency)}</span>
                                  ) : null}
                                  {line.priceIsOrderTotal && lineExVatLine != null ? (
                                    <span style={{ marginLeft: '0.5rem' }}>· Order total (ex. VAT)</span>
                                  ) : null}
                                </div>
                              </div>
                              <input
                                type="number"
                                className="quote-draft-price-input"
                                min="0"
                                step="0.01"
                                placeholder="—"
                                value={priceEx != null ? String(priceEx) : ''}
                                onChange={(e) => handlePriceExVatInput(line.key, e.target.value)}
                                disabled={quoteModalLoading || quoteSubmitting}
                                aria-label={`Price excluding VAT, line ${lineIdx + 1}`}
                              />
                              <input
                                type="number"
                                className="quote-draft-margin-input"
                                min="0"
                                max="99.9"
                                step="0.1"
                                value={lineMargin}
                                onChange={(e) => handleLineMarginChange(line.key, e.target.value)}
                                disabled={quoteModalLoading || quoteSubmitting}
                                aria-label={`Profit margin percent, line ${lineIdx + 1}`}
                              />
                              <select
                                className="quote-draft-category-select"
                                aria-label={`Category for line ${lineIdx + 1}`}
                                value={lineCategories[String(line.key)] ?? DEFAULT_QUOTE_CATEGORY}
                                onChange={(e) => handleLineCategoryChange(line.key, e.target.value)}
                                disabled={quoteModalLoading || quoteSubmitting}
                              >
                                {categorySelectOptions.map((d) => (
                                  <option key={d} value={d}>
                                    {d}
                                  </option>
                                ))}
                              </select>
                              <button
                                type="button"
                                className="quote-draft-remove-line"
                                onClick={() => handleRemoveQuoteLine(line.key)}
                                disabled={quoteModalLoading || quoteSubmitting}
                                aria-label={`Remove line ${lineIdx + 1}`}
                                title="Remove line"
                              >
                                ×
                              </button>
                            </div>
                          );
                        })}
                      </div>

                      <div className="quote-draft-totals">
                        <div className="quote-draft-totals-row muted">
                          <span>Subtotal ex VAT</span>
                          <strong>{formatQuoteAmount(quoteTotals.exVatSum)}</strong>
                        </div>
                        <div className="quote-draft-totals-row">
                          <span>Total ex VAT (per-line margins applied)</span>
                          <strong>
                            {quoteTotals.marginTooHigh
                              ? '—'
                              : formatQuoteAmount(quoteTotals.withMarginSum)}
                          </strong>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}

              {quoteSubmitError && <div className="ticket-form-error">{quoteSubmitError}</div>}
              {quoteSubmitSuccess && <div className="quote-draft-success">{quoteSubmitSuccess}</div>}

              <div className="ticket-form-actions">
                <button type="button" className="secondary" onClick={handleCloseQuoteModal} disabled={quoteSubmitting}>
                  Close
                </button>
                <button
                  type="submit"
                  disabled={
                    quoteModalLoading || quoteSubmitting || !!quoteModalError || cbcLines.length === 0
                  }
                >
                  Submit
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default Orders;
